/**
 * useAwareness.ts — the change-awareness read + mark-seen hooks (DC-4/DC-5 —
 * docs/domains/realtime-reactivity/slices/ProjectB.slice.yaml).
 *
 * `apps/api` is a server-only app package with no public exports, so the response shapes
 * below are hand-declared rather than derived via `z.infer` from
 * apps/api/src/routes/manager/awareness.schemas.ts (structurally impossible shared import) —
 * mirrors the existing convention in apps/landlord/src/lib/account.api.ts /
 * apps/landlord/src/lib/condition.api.ts. Keep these fields in sync with that schema file by
 * hand: awarenessCountsResponseSchema (byClass: Record<string, number>; byInstance:
 * { referenceType, referenceId, count, lastEventAt }[] — lastEventAt added for DC-4),
 * markSeenBodySchema, markSeenResponseSchema.
 */

import { useCallback, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { DOMAIN_EVENT_CATALOG, type DomainEventType } from '@resida/shared';
import { apiFetch } from '../lib/api';
import { useDomainEvents, type DomainEventHandlerMap } from './useDomainEvents';

/** Mirrors awarenessCountsResponseSchema's `byInstance` row. */
export interface AwarenessInstanceCount {
  referenceType: string;
  referenceId: string;
  count: number;
  /** ISO-8601 timestamp on the wire (z.date() on the Zod-inferred server type). No consumer
   *  sorts by recency yet — carried because DC-4 specifies it on the response shape. */
  lastEventAt: string;
}

/** Mirrors awarenessCountsResponseSchema (DC-4). */
export interface AwarenessCounts {
  byClass: Record<string, number>;
  byInstance: AwarenessInstanceCount[];
}

/** Mirrors markSeenBodySchema (DC-5). */
export interface MarkSeenInput {
  referenceType: string;
  referenceId: string;
}

/** Mirrors markSeenResponseSchema (DC-5). */
export interface MarkSeenResult {
  cleared: number;
}

export const AWARENESS_COUNTS_QUERY_KEY = ['awareness', 'counts'];

/**
 * DC-4 — per-class and per-instance un-cleared change-awareness counts for the authenticated
 * manager, from one request. Drives both nav badges/dots (byClass) and per-row unseen marks
 * (byInstance).
 *
 * BR-15/BR-7 — a badge reads its count from the server; an event only triggers a re-read. This
 * subscribes to EVERY DomainEvent arm (no allowlist — a new arm is reactive the day it's
 * added, same as useNotificationsRealtimeSync in useNotifications.ts), so the unseen dots/marks
 * this hook drives react live instead of waiting out staleTime.
 */
export function useUnseenCounts() {
  const queryClient = useQueryClient();

  const invalidate = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: AWARENESS_COUNTS_QUERY_KEY });
  }, [queryClient]);

  const handlers = useMemo<DomainEventHandlerMap>(() => {
    const map: DomainEventHandlerMap = {};
    for (const type of Object.keys(DOMAIN_EVENT_CATALOG) as DomainEventType[]) {
      map[type] = invalidate;
    }
    return map;
  }, [invalidate]);

  useDomainEvents(handlers);

  return useQuery({
    queryKey: AWARENESS_COUNTS_QUERY_KEY,
    queryFn: () => apiFetch<AwarenessCounts>('/landlord/api/awareness/counts'),
    staleTime: 30_000,
  });
}

/**
 * DC-5 — mark every un-seen change-awareness item for one (referenceType, referenceId)
 * instance seen, for the authenticated manager. Idempotent (BR-16). Invalidates the counts
 * query on success so badges/dots/marks reflect the clear immediately.
 */
export function useMarkSeen() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: MarkSeenInput) =>
      apiFetch<MarkSeenResult>('/landlord/api/awareness/seen', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: AWARENESS_COUNTS_QUERY_KEY });
    },
  });
}
